import { useState, useEffect, useContext } from 'react';
import {
    Box,
    Button,
    Card,
    CardActions,
    Grid,
    Typography,
} from '@mui/material';
import PublishIcon from '@mui/icons-material/Publish';
import Link from '@mui/material/Link';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttForm,
    SttSelectMship,
    UploadFileDialog,
} from '../../../sporttia/all';
import { subsetObject } from '../../../lib/utils';
import sporttiaImage from '../../../images/sporttia-icon.svg';
import ConfigurationTriggersDialog from './ConfigurationTriggersDialog';
import { formatLastVisit } from './formatLastVisit';
import {
    lockFormElements,
    useCanModifyConfiguration,
} from './useCanModifyConfiguration';

type Mship = {
    id?: number;
    fullName: string;
    user?: {
        id?: number;
    };
};

type Timezone = {
    id?: number;
};

type Customer = {
    numInvoiceFormat?: string;
};

type Logo = {
    id: number;
};

type GuestUser = {
    mship?: Mship;
};

type SC = {
    id?: number;
    name?: string;
    bulletin?: string;
    addrees?: string;
    web?: string;
    emailPrivated?: string;
    emailPublic?: string;
    phonePrivated?: string;
    phonePublic?: string;
    updatePursesOnCascadeAllowed?: boolean;
    termsAndConditions?: string;
    iban?: string;
    address?: string;
    mapX?: number;
    mapY?: number;
    logo?: Logo;
    timezone?: Timezone;
    customer?: Customer;
    guestUser?: GuestUser;
};

type SCResponse = {
    sc: SC;
};

type FileResponse = {
    file: {
        id: number;
    };
};

export default function ConfigurationMain(): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const canModify = useCanModifyConfiguration();
    const [sc, setSc] = useState<SC>({});
    const [mship, setMship] = useState<Mship>({ fullName: '' });
    const [openUploadPhoto, setOpenUploadPhoto] = useState<boolean>(false);
    const [
        openConfigurationTriggersDialog,
        setOpenConfigurationTriggersDialog,
    ] = useState<boolean>(false);

    const handleForm = ({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }): void => {
        if (!name) return;
        if (name === 'numInvoiceFormat') {
            setSc({
                ...sc,
                customer: {
                    ...sc.customer,
                    numInvoiceFormat: value as string,
                },
            });
        } else if (name === 'timezoneId') {
            setSc({
                ...sc,
                timezone: {
                    ...sc.timezone,
                    id: value as number,
                },
            });
        } else {
            setSc({ ...sc, [name]: value });
        }
    };

    const saveSportcenter = (): void => {
        const params: Record<string, unknown> = subsetObject(sc, [
            'name',
            'bulletin',
            'addrees',
            'web',
            'emailPrivated',
            'emailPublic',
            'phonePrivated',
            'phonePublic',
            'updatePursesOnCascadeAllowed',
            'termsAndConditions',
            'iban',
        ]);

        if (mship && mship.id && mship.user && mship.user.id) {
            params.idGuestUser = mship.user.id;
        }

        if (mship?.fullName === '') {
            params.idGuestUser = null;
        }

        if (sc.timezone) {
            params.idTimezone = sc.timezone.id;
        }

        cxt?.api('PUT', `/scs/${cxt.sc?.id}`, {
            params,
            success: () => {
                cxt.showMessage('S', t('Saved'));
            },
        });
    };

    const loadSportcenter = (): void => {
        cxt?.api('GET', `/scs/${cxt.sc?.id}`, {
            success: (r: SCResponse) => {
                setSc(r.sc);

                if (r.sc.guestUser && r.sc.guestUser.mship) {
                    setMship(r.sc.guestUser.mship);
                }
            },
        });
    };

    useEffect(() => {
        loadSportcenter();
    }, []);

    const lastVisit =
        cxt?.user && 'lastVisit' in cxt.user
            ? formatLastVisit(
                  (cxt.user as { lastVisit?: string | Date }).lastVisit,
              )
            : null;

    return (
        <Box>
            <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 3 }}>
                    <Card>
                        {sc.logo ? (
                            <img
                                style={{ width: '100%' }}
                                src={cxt?.getResource(
                                    `/images/${sc.logo.id}.jpg`,
                                )}
                                alt={`${sc.name} logo`}
                            />
                        ) : (
                            <Box p={2}>
                                <img
                                    style={{ width: '100%' }}
                                    src={sporttiaImage}
                                    alt="Sporttia logo"
                                />
                            </Box>
                        )}

                        {canModify && (
                            <CardActions>
                                <Button
                                    fullWidth
                                    size="small"
                                    color="primary"
                                    startIcon={<PublishIcon />}
                                    onClick={() => setOpenUploadPhoto(true)}
                                >
                                    {t('Upload')}
                                </Button>
                            </CardActions>
                        )}

                        {lastVisit && (
                            <Box p={1} mt={2}>
                                <Typography variant="body1">
                                    {t('configuration.lastVisit')}:
                                </Typography>
                                <Typography variant="body2">
                                    {lastVisit}
                                </Typography>
                            </Box>
                        )}
                    </Card>

                    <Box mt={3}>
                        <Link
                            onClick={() =>
                                setOpenConfigurationTriggersDialog(true)
                            }
                        >
                            {t('SeeIntegrationOperations')}
                        </Link>
                    </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 9 }}>
                    <SttForm
                        onChangeForm={handleForm}
                        elements={lockFormElements(
                            [
                                {
                                    md: 6,
                                    sm: 12,
                                    xs: 12,
                                    caption: t('Name'),
                                    name: 'name',
                                    value: sc.name,
                                },
                                {
                                    md: 6,
                                    sm: 12,
                                    xs: 12,
                                    caption: t('WebPage'),
                                    name: 'web',
                                    value: sc.web,
                                },
                                {
                                    md: 12,
                                    sm: 12,
                                    xs: 12,
                                    type: 'textarea',
                                    caption: t('Bulletin'),
                                    name: 'bulletin',
                                    value: sc.bulletin,
                                },
                                {
                                    md: 6,
                                    sm: 12,
                                    xs: 12,
                                    caption: t('page.sc.conf.emailPublic'),
                                    name: 'emailPublic',
                                    value: sc.emailPublic,
                                },
                                {
                                    md: 6,
                                    sm: 12,
                                    xs: 12,
                                    caption: t('page.sc.conf.emailPrivated'),
                                    name: 'emailPrivated',
                                    value: sc.emailPrivated,
                                },
                                {
                                    md: 6,
                                    sm: 12,
                                    xs: 12,
                                    caption: t('page.sc.conf.phonePublic'),
                                    name: 'phonePublic',
                                    value: sc.phonePublic,
                                },
                                {
                                    md: 6,
                                    sm: 12,
                                    xs: 12,
                                    caption: t('page.sc.conf.phonePrivated'),
                                    name: 'phonePrivated',
                                    disabled: true,
                                    value: sc.phonePrivated,
                                },
                                {
                                    md: 6,
                                    sm: 12,
                                    xs: 12,
                                    caption: t('Address'),
                                    name: 'address',
                                    value: sc.address,
                                },
                                {
                                    md: 6,
                                    sm: 12,
                                    xs: 12,
                                    type: 'component',
                                    component: (
                                        <SttSelectMship
                                            readOnly={!canModify}
                                            caption={t('GuestUser')}
                                            mship={mship as any}
                                            onSelect={(m: any) =>
                                                setMship(m as Mship)
                                            }
                                        />
                                    ),
                                },
                                {
                                    type: 'textInput',
                                    name: 'iban',
                                    caption: t('IBAN'),
                                    md: 6,
                                    sm: 12,
                                    xs: 12,
                                    value: sc.iban,
                                },
                                {
                                    xs: 12,
                                    type: 'textarea',
                                    rows: 10,
                                    caption: t('TermsAndConditions'),
                                    name: 'termsAndConditions',
                                    value: sc.termsAndConditions,
                                },
                            ],
                            !canModify,
                        )}
                        buttons={
                            canModify
                                ? [
                                      {
                                          type: 'save',
                                          onClick: () => saveSportcenter(),
                                      },
                                  ]
                                : []
                        }
                    />
                </Grid>
            </Grid>

            <UploadFileDialog
                open={openUploadPhoto}
                types={['SC_LOGO']}
                onClose={() => setOpenUploadPhoto(false)}
                onUploaded={(r: any) => {
                    const { file } = r as FileResponse;
                    setOpenUploadPhoto(false);
                    setSc({
                        ...sc,
                        logo: {
                            id: file.id,
                        },
                    });
                }}
            />

            <ConfigurationTriggersDialog
                open={openConfigurationTriggersDialog}
                onClose={() => setOpenConfigurationTriggersDialog(false)}
            />
        </Box>
    );
}
